import React, { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import MasterPage from "../master-page";
import localizationService from "../../services/localization-service";
import notification from "../../services/notification-service";
import {
  showSmsError,
  parseTimeLeftToSeconds,
} from "../../utils/sms-helpers.js";
import VerificationCodeStatus from "../../models/VerificationCodeStatus.js";
import storageService from "../../services/storage-service";
import config from "../../config";
import MoonLoader from "react-spinners/MoonLoader";
import ConfirmationModal from "../../components/modals/modal-confirm";
import SmsVerificationCodeModal from "../../components/modals/modal-sms-verification-code";
import SmsUnsubscribeModal from "../../components/modals/modal-sms-unsubscribe";
import { useLocation } from "react-router-dom";
import DOMPurify from "dompurify";
import { checkSmsStatus, setSmsData } from "../../actions/portal.action";
import { IMaskInput } from "react-imask";
import SmsState from "../../models/SmsState";
import { setSecondsLeft } from "../../actions/portal.action.js";

export const TextMessagesPage = () => {
  const location = useLocation();
  const selectedlang = useSelector((state) => state.account.lang);
  const smsData = useSelector((state) => state.portal.smsData);
  const smsDataLoading = useSelector((state) => state.portal.smsDataLoading);
  const setSMSDataLoading = useSelector(
    (state) => state.portal.setSMSDataLoading,
  );
  const dispatch = useDispatch();
  // const navigate = useNavigate();
  const [openSmsVerificationCodeModal, setOpenSmsVerificationCodeModal] =
    useState(false); // If user clicked Confirm to Opt-in. If yes, the verification code modal will be opened
  const [isRequiredFilled, setIsRequiredFilled] = useState(false); // are all required fields filled in
  const [isConsentAccepted, setIsConsentAccepted] = useState(false); // has user checked Yes
  const [phoneNumber, setPhoneNumber] = useState("");
  const [originalPhoneNumber, setOriginalPhoneNumber] = useState("");
  const phoneRef = useRef(null);
  const [consentErrorMessage, setConsentErrorMessage] = useState("");
  const [phoneErrorMessage, setPhoneErrorMessage] = useState("");
  const [isOptedIn, setIsOptedIn] = useState(null); // using for displaying either consent or the switch button and phone number input (it should also be set to true if the API method returns true when the user has already subscribed)
  const [isPhoneNumberChanged, setIsPhoneNumberChanged] = useState(false); // tells us that user typed in a new number
  const [isSwitchChecked, setIsSwitchChecked] = useState(false); // state for switch button. The state should depend on API method result in confirm method
  const [openUnsubscribeModal, setOpenUnsubscribeModal] = useState(false);
  const [
    openUnsubscribeConfirmationModal,
    setOpenUnsubscribeConfirmationModal,
  ] = useState(false);
  const [isDesktopResolution, setIsDesktopResolution] = useState(true);
  const [isClickedCloseVerificationModal, setIsClickedCloseVerificationModal] =
    useState(false);
  const [isVerificationModalClosed, setIsVerificationModalClosed] =
    useState(false);

  const secondsLeft = useSelector((state) => state.portal.secondsLeft);

  // Used to display html written in localization.js
  const rawConsentTextHtml = localizationService.getLocalizationValue(
    "consentText",
    selectedlang,
  );
  const safeConsentTextHtml = DOMPurify.sanitize(rawConsentTextHtml, {
    ADD_ATTR: ["target"],
  }); // remove ANY unsafe content

  const userId = Number(storageService.getStorageItem(config.USER_ID));
  const justConfirmedRef = useRef(false);
  const preChangePhoneRef = useRef("");
  const wasPhoneNumberChangedRef = useRef(false);
  const alreadyCancelledRef = useRef(false); // to prevent cancelling subscription process twice

  const confirm = () => {
    const isValidPhone = /^\(\d{3}\)\s\d{3}-\d{4}$/.test(phoneNumber);

    if (!isConsentAccepted && !isOptedIn) {
      let acceptConsentErrorMessage = localizationService.getLocalizationValue(
        "acceptConsentErrorMessage",
        selectedlang,
      );
      setConsentErrorMessage(acceptConsentErrorMessage);
    }

    if (!isValidPhone) {
      let phoneErrorMessage = localizationService.getLocalizationValue(
        "phoneErrorMessage",
        selectedlang,
      );
      setPhoneErrorMessage(phoneErrorMessage);
    }

    if ((!isConsentAccepted && !isOptedIn) || !isValidPhone) {
      return;
    }

    localStorage.setItem(`PhoneNumber_${userId}`, phoneNumber); // using this if user goes to another page and returns or if closes the browser, while countDown is running (while the user is blocked)

    let request = {
      userId: userId,
      cellPhoneNumber: phoneNumber,
    };

    // if -> client is accepting consent, else -> client changes phone number and already is opted in (in that case smsState should be unsubscribed at first)
    if (isRequiredFilled && !isPhoneNumberChanged) {
      request.smsState = SmsState.processing;
      justConfirmedRef.current = true; // need when user reloads the page during verification. We reset everything in that case
      dispatch(setSmsData(request))
        .then(function (response) {
          smsData.smsState = SmsState.processing; // if we don't do this, smsData in redux will not be updated and it is important to be updated if user logs out
          setOpenSmsVerificationCodeModal(true);
        })
        .catch(function (res) {
          showSmsError(selectedlang);
        });
    } else {
      preChangePhoneRef.current = smsData?.cellPhoneNumber ?? "";
      wasPhoneNumberChangedRef.current = true;
      request.smsState = SmsState.processed;
      dispatch(setSmsData(request))
        .then(function (response) {
          setIsSwitchChecked(true);
          setOpenSmsVerificationCodeModal(true);
        })
        .catch(function (res) {
          showSmsError(selectedlang);
        });
    }
  };

  const closeConfirmationModal = () => {
    setOpenUnsubscribeConfirmationModal(false);
    setIsOptedIn(false);
  };

  const cancelUnsubscribe = () => {
    setOpenUnsubscribeModal(false);
    setIsSwitchChecked(true);
  };

  const confirmUnsubscribe = () => {
    setOpenUnsubscribeModal(false);
    storageService.setStorageItem(config.JUST_UNSUBSCRIBED, true); // using this flag to prevent displaying SMS reminder modal imidiately after unsubsrcibe. When user returns to this page the modal will be opened if smsState is unsubsribed

    let request = {
      userId: Number(storageService.getStorageItem(config.USER_ID)),
      cellPhoneNumber: phoneNumber,
      smsState: SmsState.unsubscribed,
    };

    dispatch(setSmsData(request))
      .then(function (response) {
        setIsSwitchChecked(false);
        setIsConsentAccepted(false);
        setPhoneNumber(null);
        setIsPhoneNumberChanged(false);
        setOpenUnsubscribeConfirmationModal(true);
        smsData.smsState = SmsState.unsubscribed;
        localStorage.removeItem(`PhoneNumber_${userId}`);
      })
      .catch(function (res) {
        showSmsError(selectedlang);
      });
  };

  const cancelSubscriptionProcess = () => {
    alreadyCancelledRef.current = true;
    let request = {
      userId: Number(storageService.getStorageItem(config.USER_ID)),
      cellPhoneNumber: null,
      smsState: SmsState.notProcessed,
    };

    dispatch(setSmsData(request))
      .then(function (response) {
        setIsConsentAccepted(false);
        setPhoneNumber(null);
        localStorage.removeItem(`PhoneNumber_${userId}`);
      })
      .catch(function (res) {
        showSmsError(selectedlang);
      });
  };

  const toggleSwitch = () => {
    setIsSwitchChecked(!isSwitchChecked);
    setOpenUnsubscribeModal(!openUnsubscribeModal);
  };

  const createConfirmButton = () => {
    const isBlocked =
      (secondsLeft > 0 &&
        (smsData?.verificationCodeStatus ===
          VerificationCodeStatus.maxAttemptsExceeded ||
          smsData?.verificationCodeStatus ===
            VerificationCodeStatus.maxCodeRequestAttemptsExceeded)) ||
      (isOptedIn && !isPhoneNumberChanged);

    return (
      <button
        disabled={isBlocked}
        className={`inline-flex items-center justify-center ml-3 px-4 py-2 rounded-md text-sm font-medium border border-gray-300 text-white focus:ring-2 focus:outline-none focus:ring-offset-2 focus:ring-[#9aa82d] ${!isDesktopResolution ? "confirm-btn-mobile w-full mt-4" : ""} ${isBlocked ? "bg-gray-300 text-gray-500 cursor-not-allowed hover:bg-gray-300" : "bg-[#9aa82d] hover:bg-[#7d8a1f]"}`}
        onClick={() => confirm()}
      >
        {localizationService.getLocalizationValue(
          "optedInConfirmBtn",
          selectedlang,
        )}
      </button>
    );
  };

  useEffect(() => {
    if (!smsData || !smsData.blockedDateTimeLeft) return;

    const isBlocked =
      smsData.verificationCodeStatus ===
        VerificationCodeStatus.maxAttemptsExceeded ||
      smsData.verificationCodeStatus ===
        VerificationCodeStatus.maxCodeRequestAttemptsExceeded;

    if (isBlocked && parseTimeLeftToSeconds(smsData.blockedDateTimeLeft) > 0) {
      justConfirmedRef.current = true;
      setOpenSmsVerificationCodeModal(true);
    }
  }, [smsData]);

  useEffect(() => {
    if (!isOptedIn && isConsentAccepted && phoneNumber != "") {
      setIsRequiredFilled(true);
    } else if (isOptedIn && phoneNumber != "" && isPhoneNumberChanged) {
      setIsRequiredFilled(true);
    } else {
      setIsRequiredFilled(false);
    }
  }, [isConsentAccepted, phoneNumber, isPhoneNumberChanged, isOptedIn]);

  // function to remove everything except digits, in order to be able to compare old and new phone number
  const normalizePhone = (v) => {
    const normalized = String(v || "").replace(/\D/g, "");
    return normalized;
  };

  useEffect(() => {
    // Only update isOptedIn if smsData has been loaded (not empty array)
    if (smsData && smsData.smsState !== undefined) {
      if (smsData.smsState === SmsState.processed) {
        setIsOptedIn(true);
        setIsSwitchChecked(true);
        if (smsData.cellPhoneNumber !== null) {
          setPhoneNumber(smsData.cellPhoneNumber);
          setOriginalPhoneNumber(normalizePhone(smsData.cellPhoneNumber));
        }
      } else if (
        smsData.smsState === SmsState.processing &&
        isPhoneNumberChanged
      ) {
        setIsOptedIn(true);
      } else if (
        smsData.smsState === SmsState.processing &&
        !justConfirmedRef.current
      ) {
        // reset everything if user reloads the page during verification
        const isBlocked =
          smsData.verificationCodeStatus ===
            VerificationCodeStatus.maxAttemptsExceeded ||
          smsData.verificationCodeStatus ===
            VerificationCodeStatus.maxCodeRequestAttemptsExceeded;
        if (!isBlocked && !alreadyCancelledRef.current) {
          cancelSubscriptionProcess();
          localStorage.removeItem(`PhoneNumber_${userId}`);
        }
        setIsOptedIn(false);
      } else {
        setIsOptedIn(false);
      }
    }
  }, [smsData]);

  useEffect(() => {
    const isBlocked =
      smsData.verificationCodeStatus ===
        VerificationCodeStatus.maxAttemptsExceeded ||
      smsData.verificationCodeStatus ===
        VerificationCodeStatus.maxCodeRequestAttemptsExceeded;

    if (smsData.smsState === SmsState.processing && !isBlocked) {
      return () => {
        // This runs when component unmounts (user leaves page)
        if (!alreadyCancelledRef.current) {
          cancelSubscriptionProcess();
        }
      };
    }
  }, []);

  useEffect(() => {
    if (window.innerWidth < 500) {
      setIsDesktopResolution(false);
    }
    storageService.setStorageItem(config.SMS_CLICKED_YES, false);

    if (phoneNumber === null || phoneNumber === "") {
      setPhoneNumber(localStorage.getItem(`PhoneNumber_${userId}`));
    }
  }, []);

  return (
    <MasterPage>
      <React.Fragment>
        {smsDataLoading || isOptedIn === null ? (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 transition-opacity duration-300">
            <MoonLoader
              color="#9aa82d"
              size={80}
              loading={true}
              aria-label="Loading Spinner"
            />
          </div>
        ) : (
          <>
            {setSMSDataLoading && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <MoonLoader
                  color="#9aa82d"
                  size={80}
                  loading={true}
                  aria-label="Loading Spinner"
                />
              </div>
            )}
            <div
              className={`text-messages-page bg-white ${
                isDesktopResolution ? "rounded-2xl shadow-xl p-8" : "px-1"
              } ${
                isOptedIn === true
                  ? "max-w-[450px] mx-auto flex justify-center"
                  : ""
              } ${isOptedIn === true && isDesktopResolution ? "flex-col" : ""} 
              ${
                isOptedIn === true && !isDesktopResolution
                  ? "min-h-[calc(100dvh-114px)]"
                  : ""
              }
              `}
            >
              <div>
                <h1
                  className={`consent-title my-[20px] ${
                    isOptedIn === true ? "text-center" : ""
                  } ${isDesktopResolution ? "text-xl" : "text-lg"}`}
                >
                  {isOptedIn === false
                    ? localizationService.getLocalizationValue(
                        "consentTitle",
                        selectedlang,
                      )
                    : localizationService.getLocalizationValue(
                        "optedInTitle",
                        selectedlang,
                      )}
                </h1>
                {isOptedIn === true && (
                  <>
                    <p className="pb-2">
                      {localizationService.getLocalizationValue(
                        "optedInTextPartOne",
                        selectedlang,
                      )}
                    </p>
                    <p className="pb-2">
                      {localizationService.getLocalizationValue(
                        "optedInTextPartTwo",
                        selectedlang,
                      )}
                    </p>
                  </>
                )}
                {isOptedIn === false && (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: safeConsentTextHtml,
                    }}
                  ></div>
                )}
                <div className="flex items-center mt-6">
                  {isOptedIn === false ? (
                    <div className="flex items-center mr-4">
                      <label className="consent-checkbox-label inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={
                            isConsentAccepted ||
                            smsData.smsState === SmsState.processing
                          }
                          onChange={(e) => {
                            setIsConsentAccepted(e.target.checked);
                            setConsentErrorMessage("");
                          }}
                        />

                        <span
                          className="w-5 h-5 border-2 border-gray-400 rounded 
                                  peer-checked:bg-[#9aa82d] peer-checked:border-[#9aa82d]
                                  relative flex items-center justify-center
                                  after:content-[''] after:w-2.5 after:h-1.5
                                  after:border-l-2 after:border-b-2 after:border-white
                                  after:-rotate-45 after:opacity-0
                                  after:-translate-y-[1px]
                                  peer-checked:after:opacity-100"
                        ></span>

                        <span className="ml-2 select-none">
                          {localizationService.getLocalizationValue(
                            "yes",
                            selectedlang,
                          )}
                        </span>
                      </label>
                    </div>
                  ) : (
                    <div className="mr-3">
                      <label className="flex cursor-pointer select-none items-center mb-0 mr-2">
                        <div className="relative">
                          <input
                            type="checkbox"
                            checked={isSwitchChecked}
                            onChange={toggleSwitch}
                            className="sr-only"
                          />
                          <div
                            className={`block h-8 w-14 rounded-full ${
                              isSwitchChecked ? "bg-[#9aa82d]" : "bg-gray-300"
                            }`}
                          ></div>
                          <div
                            className={`dot absolute left-1 top-1 flex h-6 w-6 items-center justify-center 
                          rounded-full bg-white transition 
                          ${
                            isSwitchChecked
                              ? "translate-x-6 text-[#8a9728]"
                              : "text-gray-400"
                          }`}
                          >
                            <span
                              className={isSwitchChecked ? "active" : "hidden"}
                            >
                              <svg
                                width="11"
                                height="8"
                                viewBox="0 0 11 8"
                                fill="currentColor"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M10.0915 0.951972L10.0867 0.946075L10.0813 0.940568C9.90076 0.753564 9.61034 0.753146 9.42927 0.939309L4.16201 6.22962L1.58507 3.63469C1.40401 3.44841 1.11351 3.44879 0.932892 3.63584C0.755703 3.81933 0.755703 4.10875 0.932892 4.29224L0.932878 4.29225L0.934851 4.29424L3.58046 6.95832C3.73676 7.11955 3.94983 7.2 4.1473 7.2C4.36196 7.2 4.55963 7.11773 4.71406 6.9584L10.0468 1.60234C10.2436 1.4199 10.2421 1.1339 10.0915 0.951972ZM4.2327 6.30081L4.2317 6.2998C4.23206 6.30015 4.23237 6.30049 4.23269 6.30082L4.2327 6.30081Z"
                                  fill="currentColor"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                ></path>
                              </svg>
                            </span>
                            <span
                              className={
                                !isSwitchChecked ? "inactive" : "hidden"
                              }
                            >
                              <svg
                                className="h-4 w-4 stroke-current"
                                fill="none"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="3"
                                  d="M6 18L18 6M6 6l12 12"
                                ></path>
                              </svg>
                            </span>
                          </div>
                        </div>
                      </label>
                    </div>
                  )}
                  <div
                    className={`relative w-[240px] ${!isDesktopResolution ? "grow" : ""}`}
                  >
                    <div className="absolute inset-y-0 start-0 top-0 flex items-center ps-3.5 pointer-events-none">
                      <svg
                        className="w-4 h-4 text-body"
                        aria-hidden="true"
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke="currentColor"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M18.427 14.768 17.2 13.542a1.733 1.733 0 0 0-2.45 0l-.613.613a1.732 1.732 0 0 1-2.45 0l-1.838-1.84a1.735 1.735 0 0 1 0-2.452l.612-.613a1.735 1.735 0 0 0 0-2.452L9.237 5.572a1.6 1.6 0 0 0-2.45 0c-3.223 3.2-1.702 6.896 1.519 10.117 3.22 3.221 6.914 4.745 10.12 1.535a1.601 1.601 0 0 0 0-2.456Z"
                        />
                      </svg>
                    </div>
                    <IMaskInput
                      inputRef={phoneRef}
                      mask="(000) 000-0000"
                      value={phoneNumber}
                      onAccept={(value) => {
                        setPhoneNumber(value);
                        setPhoneErrorMessage("");
                        setIsPhoneNumberChanged(
                          isOptedIn &&
                            originalPhoneNumber !== "" &&
                            originalPhoneNumber !== null &&
                            originalPhoneNumber !== undefined &&
                            normalizePhone(value) !== originalPhoneNumber,
                        );
                      }}
                      placeholder="(416) 123-4567"
                      className={`block w-full ps-9 pe-3 py-2 bg-neutral-secondary-medium border border-slate-200 rounded-md text-heading text-sm rounded-base focus:ring-brand focus:border-brand shadow-sm focus:shadow placeholder:text-body ${isOptedIn ? "bg-gray-200 cursor-not-allowed" : ""}`}
                      pattern="^\(\d{3}\)\s\d{3}-\d{4}$"
                      disabled={isOptedIn}
                    />
                  </div>
                  {isDesktopResolution && createConfirmButton()}
                </div>
                {/*on mobile phone the button needs to be on separate line*/}
                {!isDesktopResolution && createConfirmButton()}{" "}
                {isOptedIn && (
                  <div className="w-full mt-[40px] text-center">
                    <a href="/terms" className="terms-of-use-link">
                      {localizationService.getLocalizationValue(
                        "termsOfUse",
                        selectedlang,
                      )}
                    </a>
                  </div>
                )}
                {consentErrorMessage && (
                  <p className="pt-2 text-red-500">* {consentErrorMessage}</p>
                )}
                {phoneErrorMessage && (
                  <p className="pt-2 text-red-500">* {phoneErrorMessage}</p>
                )}
              </div>

              {/* Verification Code modal */}
              <SmsVerificationCodeModal
                isOpen={openSmsVerificationCodeModal}
                onClose={(isSuccess) => {
                  setOpenSmsVerificationCodeModal(false);
                  setIsClickedCloseVerificationModal(true);
                  if (isSuccess) {
                    setTimeout(() => {
                      setIsRequiredFilled(false);
                      setIsOptedIn(true);
                    }, 0);
                    setIsSwitchChecked(true);
                    localStorage.removeItem(`PhoneNumber_${userId}`);
                  } else if (wasPhoneNumberChangedRef.current) {
                    wasPhoneNumberChangedRef.current = false;
                    // User closed modal during phone number change — revert displayed number
                    setPhoneNumber(preChangePhoneRef.current);
                    setIsPhoneNumberChanged(false);
                    localStorage.removeItem(`PhoneNumber_${userId}`);
                  }
                }}
                phoneNumber={
                  phoneNumber !== null && phoneNumber !== ""
                    ? phoneNumber
                    : localStorage.getItem(`PhoneNumber_${userId}`)
                }
                isPhoneNumberChanged={isPhoneNumberChanged}
              />

              {/* Unsubscribe modal */}
              <SmsUnsubscribeModal
                isOpen={openUnsubscribeModal}
                onClose={() => cancelUnsubscribe()}
                onConfirm={() => confirmUnsubscribe()}
              />

              {/* Unsubscribe Confirmation modal */}
              <ConfirmationModal
                isOpen={openUnsubscribeConfirmationModal}
                onClose={() => closeConfirmationModal()}
                title={localizationService.getLocalizationValue(
                  "unsubscribeConfirmationModalTitle",
                  selectedlang,
                )}
                text1={localizationService.getLocalizationValue(
                  "unsubscribeConfirmationModalText",
                  selectedlang,
                )}
                lang={selectedlang}
                localizationService={localizationService}
              />
            </div>
          </>
        )}
      </React.Fragment>
    </MasterPage>
  );
};
